import { AUTHENTICATION_METHOD } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { strings as S } from './strings'
import { userManualUrl } from '../../lib/media'

/**
 * Login endpoints — `Features/Auth/Login/Endpoint.cs`.
 *
 *   getLoginConfig       -> GET  /api/login                {}
 *                           -> LoginPageViewModel
 *   authenticate         -> POST /Login/Authenticate       { Username, Password, RememberMe }
 *                           -> { Result, Message, RequiresOtp, ErrorCode, AccessToken, ExpiresAt }
 *   checkOtp             -> POST /Login/ValidateOtp        { Username, Otp, RememberMe }
 *   resendOtp            -> POST /Login/ResendOtp          { Username }
 *   requestPasswordReset -> POST /ResetPassword/NewRequest ?username=   <- QUERY, not a body
 *   sendLoginLink        -> POST /Login/SendLoginLink       { Email }
 *
 * THE TOKEN NEVER TOUCHES THIS FILE. A successful sign-in makes the server set the
 * HttpOnly `AccessToken` cookie in its response; `AccessToken` comes back in the JSON
 * as well, and it is deliberately ignored — storing it anywhere the SPA can read would
 * throw away the protection the HttpOnly cookie exists to give.
 *
 * WHERE THE USER LANDS is the server's decision, not this app's. `GET /Default` 302s to
 * `/ManageCompanies`, `/General` or `/Courses` depending on role, so sign-in ends with a
 * real navigation there — see `goToLanding()`.
 *
 * The three POST routes answer 200 with `{ Result: false, Message }` when they mean
 * "no", which `src/lib/api.js` raises as an `ApiError`. Each call below turns that back
 * into the `{ status, message }` its screen already reads, so no component changed shape.
 */

/**
 * What the screen falls back to while the config is loading, or if it cannot be read.
 *
 * Every flag is the CAUTIOUS value: no optional links, no logo, local authentication.
 * A login form that renders a "register" link the installation does not offer is worse
 * than one that renders nothing extra.
 */
export const DEFAULT_LOGIN_CONFIG = {
  logoOnLoginPage: false,
  logoBackgroundEnabled: false,
  activateResetPasswordPage: false,
  userGuideLinkOnTheLoginPage: false,
  showRegistrationLinkOnLoginPage: false,
  enableIntroVideo: false,
  otpEnabled: false,
  ldapDataSources: [],
  entraDataSources: [],
  authMethod: AUTHENTICATION_METHOD.LOCAL,
  brand: null,
}

/** LoginPageViewModel -> the shape the screen already consumes. */
function toConfig(dto) {
  return {
    // These two are strings on the wire, not booleans — the view model carries the raw
    // Settings values (`"true"` / `"false"`) for the logo pair only.
    logoOnLoginPage: dto.LogoOnLoginPage === 'true',
    logoBackgroundEnabled: dto.LogoBackgroundEnabled === 'true',
    activateResetPasswordPage: Boolean(dto.ActivateResetPasswordPage),
    userGuideLinkOnTheLoginPage: Boolean(dto.UserGuideLinkOnTheLoginPage),
    showRegistrationLinkOnLoginPage: Boolean(dto.ShowRegistrationLinkOnLoginPage),
    enableIntroVideo: Boolean(dto.EnableIntroVideo),
    otpEnabled: Boolean(dto.OtpEnabled),
    authMethod: Number(dto.AuthMethod ?? AUTHENTICATION_METHOD.LOCAL),
    brand: {
      main: dto.PrimaryColor ?? '',
      active: dto.ActiveColor ?? '',
    },
    ldapDataSources: (dto.LdapDataSources ?? []).map((source) => ({
      id: String(source.Id),
      arabicName: source.ArabicName ?? '',
      englishName: source.EnglishName ?? '',
    })),
    entraDataSources: (dto.EntraDataSources ?? []).map((source) => ({
      id: String(source.Id),
      arabicName: source.ArabicName ?? '',
      englishName: source.EnglishName ?? '',
    })),
  }
}

export async function getLoginConfig(signal) {
  return toConfig((await api.get('/api/login', { signal })) ?? {})
}

/** Legacy: root/UserManual/user_man_ar.mp4 when the intro video is on, else the .pdf. */
export function userManualHref(config) {
  return userManualUrl(`user_man_ar${config.enableIntroVideo ? '.mp4' : '.pdf'}`)
}

/**
 * @param {object} credentials
 * @param {string} credentials.username
 * @param {string} credentials.password
 * @param {boolean} credentials.isPersistent
 * @param {string} [credentials.selectedAdProviderId]
 */
export async function authenticate({ username, password, isPersistent, selectedAdProviderId }) {
  try {
    const data = await api.post('/Login/Authenticate', {
      Username: username,
      Password: password,
      RememberMe: isPersistent,
      SelectedAdProviderId: selectedAdProviderId || null,
    })

    return {
      status: true,
      // The password was right; the emailed code finishes the sign-in.
      useOtp: Boolean(data?.RequiresOtp),
      message: data?.Message ?? '',
    }
  } catch (error) {
    return { status: false, message: messageOf(error) }
  }
}

export async function checkOtp(otp) {
  const pending = getPendingOtp()

  try {
    await api.post('/Login/ValidateOtp', {
      Username: pending?.username ?? '',
      Otp: otp,
      RememberMe: pending?.rememberMe ?? false,
    })

    return { status: true, message: '' }
  } catch (error) {
    return { status: false, message: messageOf(error) }
  }
}

export async function resendOtp() {
  const pending = getPendingOtp()

  try {
    const data = await api.post('/Login/ResendOtp', { Username: pending?.username ?? '' })
    return { Result: true, Message: data?.Message ?? S.messages.newOtp }
  } catch (error) {
    return { Result: false, Message: messageOf(error) }
  }
}

/** The username rides in the QUERY STRING: the endpoint binds a bare `string username`. */
export async function requestPasswordReset(username) {
  try {
    const data = await api.post(`/ResetPassword/NewRequest?username=${encodeURIComponent(username)}`)
    return { Result: true, Message: data?.Message ?? S.messages.resetLinkSent }
  } catch (error) {
    return { Result: false, Message: messageOf(error) }
  }
}

export async function sendLoginLink(email) {
  // Keep the sign-in screen recoverable when a customer's SMTP relay is unreachable.
  // The server has its own shorter transport timeout; this is the browser-side final guard.
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 20_000)
  try {
    const data = await api.post(
      '/Login/SendLoginLink',
      { Email: email.trim() },
      { signal: controller.signal },
    )
    return {
      status: Boolean(data?.status),
      message: data?.message ?? S.messages.loginLinkSent,
    }
  } catch (error) {
    return { status: false, message: messageOf(error) }
  } finally {
    window.clearTimeout(timeout)
  }
}

/**
 * The reset link's own page state: whether the feature is on and the link names a real
 * user. A refusal is a `Results.Problem`, so it arrives as a thrown ApiError carrying the
 * server's reason — which is what the screen shows instead of a form.
 */
export async function getResetPasswordPage({ username, token }, signal) {
  const query = new URLSearchParams({ username: username ?? '', token: token ?? '' })
  const data = await api.get(`/api/reset-password?${query}`, { signal })

  return {
    username: data?.Username ?? username ?? '',
    token: data?.Token ?? token ?? '',
    appTitle: data?.DefaultApplicationTitle ?? '',
  }
}

/**
 * The username and token are sent EXPLICITLY.
 *
 * The retired Razor page stashed both in the server session and this action read them
 * back from there. The command has always accepted them in the body, so passing them is
 * not a change to the endpoint — it just removes a dependency on server session state
 * that a SPA has no reason to rely on.
 */
export async function resetPassword({ username, token, password }) {
  try {
    const data = await api.post('/ResetPassword/Reset', {
      Username: username,
      Token: token,
      Password: password,
    })
    return { Result: true, Message: data?.Message ?? '' }
  } catch (error) {
    return { Result: false, Message: messageOf(error) }
  }
}

/**
 * Hand the browser to the server so IT decides the landing page.
 *
 * `GET /Default` 302s per role — `/ManageCompanies`, `/General` or `/Courses` — and the
 * SPA then serves whichever that is (`legacyRoutes.js` maps all three). A real
 * navigation rather than a router push, for two reasons: a `fetch` cannot read the
 * `Location` of a redirect it is told not to follow, and the page load re-runs the
 * session bootstrap so the shell starts with the freshly signed-in user.
 */
export function goToLanding() {
  window.location.assign('/Default')
}

/*
 * The half-authenticated user, between Authenticate and ValidateOtp.
 *
 * The legacy kept this in the server session. Here the OTP endpoints take the username
 * as a parameter, so the pending sign-in lives in `sessionStorage`: it survives the
 * navigation to the OTP screen and a refresh of it, and dies with the tab. It holds a
 * username and a checkbox — never a credential.
 */
const PENDING_OTP_KEY = 'rawam.login.pendingOtp'

export function setPendingOtpUser(username, rememberMe = false) {
  sessionStorage.setItem(PENDING_OTP_KEY, JSON.stringify({ username, rememberMe }))
}

export function getPendingOtp() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PENDING_OTP_KEY) ?? 'null')
    return stored?.username ? stored : null
  } catch {
    return null
  }
}

/** The OTP screen shows whose sign-in it is finishing. */
export function getPendingOtpUser() {
  return getPendingOtp()?.username ?? null
}

export function clearPendingOtpUser() {
  sessionStorage.removeItem(PENDING_OTP_KEY)
}

/**
 * A refusal from a handler is shown in the server's own words (W6). Anything else — the
 * network, an HTML page where JSON was expected — is not a sentence written for a user,
 * so it becomes the generic system message.
 */
function messageOf(error) {
  if (error instanceof ApiError && error.code === 'RESULT' && error.message) {
    return error.message
  }
  if (error instanceof ApiError && error.code === 'PROBLEM' && error.message) {
    return error.message
  }
  return S.messages.systemError
}

/** Legacy stores the username under this key when "remember me" is ticked. */
export const SAVED_USERNAME_KEY = 'rawam.login.savedUserName'
